import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Plus,
  X,
  Send,
  ArrowUpCircle,
  ArrowRightCircle,
  ArrowDownCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTaskBoard } from "@/hooks/useTaskBoard";
import { useWebsites } from "@/hooks/useWebsites";
import { useToast } from "@/hooks/use-toast";

interface NewTaskFormProps {
  onBack: () => void;
  onCreated: () => void;
}

const priorities = [
  { value: "high" as const, label: "Wysoki", icon: ArrowUpCircle, color: "text-red-500", bg: "border-red-300 bg-red-50" },
  { value: "medium" as const, label: "Sredni", icon: ArrowRightCircle, color: "text-yellow-500", bg: "border-yellow-300 bg-yellow-50" },
  { value: "low" as const, label: "Niski", icon: ArrowDownCircle, color: "text-blue-400", bg: "border-blue-300 bg-blue-50" },
];

export default function NewTaskForm({ onBack, onCreated }: NewTaskFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [checklistItems, setChecklistItems] = useState<string[]>([]);
  const [newItem, setNewItem] = useState("");
  const [websiteId, setWebsiteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { createTask } = useTaskBoard();
  const { websites } = useWebsites();
  const { toast } = useToast();

  const addChecklistItem = () => {
    if (!newItem.trim()) return;
    setChecklistItems([...checklistItems, newItem.trim()]);
    setNewItem("");
  };

  const removeChecklistItem = (index: number) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addChecklistItem();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast({
        title: "Blad",
        description: "Tytul jest wymagany",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    const { error } = await createTask(
      title.trim(),
      description.trim(),
      priority,
      checklistItems,
      websiteId
    );

    if (error) {
      toast({ title: "Blad", description: error, variant: "destructive" });
    } else {
      toast({ title: "Task utworzony" });
      onCreated();
    }

    setSubmitting(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <CardTitle className="text-lg">Nowy task</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Tytul */}
          <div className="space-y-2">
            <Label htmlFor="task-title">Tytul</Label>
            <Input
              id="task-title"
              placeholder="Co trzeba zrobic?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>

          {/* Opis */}
          <div className="space-y-2">
            <Label htmlFor="task-desc">Opis (opcjonalnie)</Label>
            <Textarea
              id="task-desc"
              placeholder="Dodatkowe szczegoly..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          {/* Priorytet */}
          <div className="space-y-2">
            <Label>Priorytet</Label>
            <div className="flex gap-2">
              {priorities.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setPriority(p.value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      priority === p.value
                        ? `${p.bg} ring-2 ring-offset-1 ring-primary/30`
                        : "border-border hover:border-primary/30"
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${p.color}`} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Domena */}
          {websites.length > 0 && (
            <div className="space-y-2">
              <Label>Domena (opcjonalnie)</Label>
              <Select
                value={websiteId || "__none__"}
                onValueChange={(v) => setWebsiteId(v === "__none__" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz domene..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Brak</SelectItem>
                  {websites.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Checklista */}
          <div className="space-y-2">
            <Label>Checklista (opcjonalnie)</Label>
            <div className="space-y-2">
              {checklistItems.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2"
                >
                  <Checkbox disabled checked={false} />
                  <span className="flex-1 text-sm">{item}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={() => removeChecklistItem(index)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}

              <div className="flex gap-2">
                <Input
                  placeholder="Dodaj pozycje checklisty..."
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={addChecklistItem}
                  disabled={!newItem.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Przyciski */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onBack}>
              Anuluj
            </Button>
            <Button type="submit" disabled={submitting || !title.trim()}>
              <Send className="h-4 w-4 mr-1" />
              {submitting ? "Tworzenie..." : "Utworz task"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
